import './configuration.less';

import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';

import { AntRadioGroup } from 'controls/react/ant/antRadio';
import { getTargetTypes } from 'areas/assets/monitors/helpers';
import { AgentDropDown, AssetDropDown } from 'controls/react';
import { MonitorBase } from './monitorBase';
import { MonitorTarget } from './monitorTarget';
import { AntInput } from 'controls/react/ant/antInput';
import { AntInputNumber } from 'controls/react/ant/antInputNumber';
import { Toolbar, ToolbarItemPosition } from 'controls/react/layout/toolbar';
import { AntButton } from 'controls/react/ant/antButton';
import { FormEntryNew, HelpIcon } from 'controls/react/form/formEntryNew';
import { getSeverityDataList, Severity } from 'framework/entities/healthData';
import { SeverityLabel } from 'controls/react/severityIndicator';
import { AntSelect, fromIdName } from 'controls/react/ant/antSelect';
import { AssetsDataset, getAssets, GetAssetsOptions } from 'areas/assets/api';
import { Link } from 'controls/react/link';
import { AssetsRouter } from 'areas/assets/bundleDescription';
import { AssetGroupRouter } from 'areas/assetgroups/bundleDescription';
import { apiFetch, apiFetchObservable } from 'framework/api';
import Configuration from 'configuration';
import { inputWithAction } from 'controls/react/form/common';
import { AddAsset } from 'controls/react/windows';
import type { SizeType } from 'antd/lib/config-provider/SizeContext';
import { useAssetGroups } from 'controls/react/assetGroupMultiSelect';
import { i18n } from 'core/localization';
import { useAssetGroupAssets } from 'areas/assetgroups/api';
import { AddAssetGroupButton, AssetGroupMonitorEditProps } from 'areas/monitors/components/AddAssetGroupButton';
import { makeAutoObservable } from 'mobx';
import { MobxManager } from 'framework/mobx-integration';
import { Link as LinkModel } from 'core/react/links';
import { useStore } from 'core/react/useStore';
import b_ from 'b_';

export interface MonitorConfigurationEntryProps<
	TMonitor extends MonitorBase<TMonitor>,
	TKeys extends keyof MonitorBase<TMonitor> = keyof MonitorBase<TMonitor>,
> {
	monitor: Pick<MonitorBase<TMonitor>, TKeys>;
	target?: MonitorTarget;
	editable?: boolean;
	systemType?: string;
	size?: SizeType;
	width?: number;
}

interface MonitorTemplateAssetProps<TMonitor extends MonitorBase<TMonitor>>
	extends MonitorConfigurationEntryProps<TMonitor> {
	agentsOnly?: boolean;
	size?: SizeType;
	width?: number;
}

const entryDisabled = <TMonitor extends MonitorBase<TMonitor>>(props: MonitorConfigurationEntryProps<TMonitor>) =>
	props.editable === false;

export const MonitorAsset = observer(
	<TMonitor extends MonitorBase<TMonitor>>(props: MonitorConfigurationEntryProps<TMonitor>) => {
		return (
			<FormEntryNew label={i18n('Asset')} model={props.target} modelField={'targetId'}>
				<AssetDropDown disabled={entryDisabled(props)} canAdd={true} accountId={props.monitor.accountId} />
			</FormEntryNew>
		);
	},
);

export const MonitorAgent = observer(
	<TMonitor extends MonitorBase<TMonitor>>(props: MonitorConfigurationEntryProps<TMonitor>) => {
		return (
			<FormEntryNew label={i18n('Agent')} model={props.target} modelField={'targetId'}>
				<AgentDropDown accountId={props.monitor.accountId} />
			</FormEntryNew>
		);
	},
);

interface MonitorAgentAssetProps<TMonitor extends MonitorBase<TMonitor>>
	extends MonitorConfigurationEntryProps<TMonitor> {
	sharedAgents?: boolean;
	onInfoChanged?: (data: { text: string; value: string }) => void;
	monitorType?: string;
	systemType?: string;
}

export const MonitorAgentAsset = observer(
	<TMonitor extends MonitorBase<TMonitor>>(props: MonitorAgentAssetProps<TMonitor>) => {
		const requestOptions: GetAssetsOptions = React.useMemo(() => {
			const config = Configuration.installedMonitorSettings as {
				[id: string]: { sharedAgents: boolean };
			};
			return {
				agentsOnly: true,
				shared: props.sharedAgents ?? config[props.monitorType]?.sharedAgents ?? false,
				dataset: AssetsDataset.Lite,
				accountId: props.monitor.accountId,
				systemType: props.systemType,
			};
		}, []);

		const assets = apiFetchObservable(getAssets(requestOptions));

		const options = React.useMemo(() => {
			return assets.data.map((x) => ({
				value: x.id,
				label: (
					<Link enabled={x.id == props.target.targetId} url={AssetsRouter.details(x.id)}>
						{x.name + (x.shared ? ' (*)' : '')}
					</Link>
				),
				searchField: x.name,
			}));
		}, [assets.data, props.target.targetId]);

		return (
			<FormEntryNew label={i18n('Asset')} model={props.target} modelField={'targetId'}>
				<AntSelect options={options} optionFilterProp={'searchField'} loading={assets.loading} />
			</FormEntryNew>
		);
	},
);

interface TargetAssetProps<TMonitor extends MonitorBase<TMonitor>> extends MonitorAgentAssetProps<TMonitor> {
	includeSubaccounts?: boolean;
	systemType?: string;
}

export const TargetAssetSelect = observer(
	<TMonitor extends MonitorBase<TMonitor>>(props: TargetAssetProps<TMonitor>) => {
		const [assets, setAssets] = useState([]);
		const [loading, setLoading] = useState(false);

		const requestOptions: GetAssetsOptions = React.useMemo(() => {
			const config = Configuration.installedMonitorSettings as {
				[id: string]: { sharedAgents: boolean };
			};
			return {
				agentsOnly: props.monitorType == 'AGENT',
				shared: props.sharedAgents ?? config[props.monitorType]?.sharedAgents ?? false,
				accountId: props.monitor.accountId,
				includeSubaccounts: props.includeSubaccounts,
				systemType: props.systemType,
			};
		}, []);

		const loadAssets = async (requestOptions: GetAssetsOptions) => {
			setLoading(true);
			const data = await apiFetch(getAssets(requestOptions));
			if (data.success) {
				setAssets(data.data);
			}
			setLoading(false);
		};

		const options = React.useMemo(() => {
			return assets.map((x) => ({
				value: x.id,
				label: (
					<Link enabled={x.id == props.target.targetId} url={AssetsRouter.details(x.id)}>
						{(x.name += x.shared ? ' (*)' : '')}
					</Link>
				),
			}));
		}, [assets, props.target.targetId]);

		useEffect(() => {
			loadAssets(requestOptions);
		}, [requestOptions]);

		const refresh = useCallback(() => {
			loadAssets(requestOptions);
		}, [requestOptions]);

		const onCreateNew = useCallback((value: string) => {
			props.target.targetId = value;
		}, []);

		return (
			<FormEntryNew label={i18n('Asset')} model={props.target} modelField={'targetId'} className="select-with-action">
				<AntSelect disabled={entryDisabled(props)} options={options} loading={loading} />
				<AddAsset
					onClose={refresh}
					accountId={props.monitor.accountId}
					containerClass={inputWithAction('action')}
					disabled={entryDisabled(props)}
					onCreateNew={onCreateNew}
				/>
			</FormEntryNew>
		);
	},
);

export const MonitorMeasuringAssetType = observer(
	<TMonitor extends MonitorBase<TMonitor>>(props: MonitorConfigurationEntryProps<TMonitor>) => {
		return (
			<FormEntryNew label={i18n('target-type')} model={props.target} modelField={'type'}>
				<AntRadioGroup dataList={getTargetTypes()} buttonStyle={'solid'} disabled={entryDisabled(props)} />
			</FormEntryNew>
		);
	},
);

export const MonitorForCreatedAssetGroupKey = 'monitor-for-created-asset-group';
export const CreatedAssetGroupForCachedMonitorKey = 'created-asset-group-for-cached-monitor';
export type MonitorAssetGroupProps<TMonitor extends MonitorBase<TMonitor>> = MonitorConfigurationEntryProps<TMonitor> &
	AssetGroupMonitorEditProps<TMonitor> & { tooltip?: string };

export const MonitorAssetGroup = observer(
	<TMonitor extends MonitorBase<TMonitor>>(props: MonitorAssetGroupProps<TMonitor>) => {
		//const groups = apiFetchObservable(getAssetGroups(requestOptions));
		const [groups, loading] = useAssetGroups(
			(typeof props.monitor == 'function' ? props.monitor() : props.monitor).accountId,
		);

		const onAdded = useCallback(
			(targetId: string) => {
				props.target.targetId = targetId;
			},
			[props.target.targetId],
		);

		const options = React.useMemo(() => {
			return groups.map((x) => ({
				value: x.id,
				label: (
					<Link enabled={x.id == props.target.targetId} url={AssetGroupRouter.details(x.id)}>
						{x.name}
					</Link>
				),
				searchField: x.name,
			}));
		}, [groups, props.target.targetId]);

		return (
			<FormEntryNew
				label={i18n('Asset Group')}
				model={props.target}
				modelField={'targetId'}
				disabled={entryDisabled(props)}
				className={'asset-group-select'}
				headerAdditionalContent={props.tooltip ? <HelpIcon tooltip={props.tooltip} /> : null}
			>
				<AntSelect options={options} optionFilterProp={'searchField'} loading={loading} />
				{!entryDisabled(props) && (
					<AddAssetGroupButton
						monitor={props.monitor}
						onChange={onAdded}
						ignoreType={props.ignoreType}
						onClick={props.onClick}
					/>
				)}
			</FormEntryNew>
		);
	},
);

export class MonitorAssetGroupWithLinkStore {
	model: MonitorTarget;
	mobx = new MobxManager();
	constructor(private link: LinkModel<string>) {
		this.link = link;
		makeAutoObservable(this);
		this.mobx.reaction(
			() => this.model.targetId,
			() => {
				this.link.update(this.model.targetId);
			},
		);
		this.link.changed(() => {
			this.model.targetId = this.link.value;
		});
		this.model = { targetId: this.link.value } as MonitorTarget;
	}

	destroy() {
		this.mobx.destroy();
	}
}
export const MonitorAssetGroupWithLink = observer(
	<TMonitor extends MonitorBase<TMonitor>>({
		link,
		monitor,
		onAddClick,
		tooltip,
		disabled,
	}: {
		link: LinkModel<string>;
		monitor: TMonitor;
		onAddClick: () => void;
		tooltip?: string;
		disabled?: boolean;
	}) => {
		const store = useStore(() => new MonitorAssetGroupWithLinkStore(link));
		return (
			<MonitorAssetGroup
				monitor={monitor}
				target={store.model}
				ignoreType={true}
				onClick={onAddClick}
				tooltip={tooltip}
				editable={!disabled}
			/>
		);
	},
);

const MonitorTemplateAssetBase = observer(
	<TMonitor extends MonitorBase<TMonitor>>(props: MonitorTemplateAssetProps<TMonitor>) => {
		const dataHolder = useAssetGroupAssets({
			accountId: props.monitor.accountId,
			assetGroupId: props.target.targetId,
			agentsOnly: props.agentsOnly,
			systemType: props.systemType,
		});

		return (
			<FormEntryNew
				label={i18n('Template Asset')}
				model={props.target}
				modelField={'templateAssetId'}
				disabled={props.editable === false}
			>
				<AntSelect
					size={props.size}
					width={props.width}
					disabled={!dataHolder.loaded}
					options={fromIdName(dataHolder.data)}
					loading={dataHolder.loading}
				/>
			</FormEntryNew>
		);
	},
);

export const MonitorTemplateAgentAsset = observer(
	<TMonitor extends MonitorBase<TMonitor>>(props: MonitorConfigurationEntryProps<TMonitor>) => {
		return <MonitorTemplateAssetBase {...props} agentsOnly={true} />;
	},
);

export const MonitorTemplateAsset = observer(
	<TMonitor extends MonitorBase<TMonitor>>(props: MonitorConfigurationEntryProps<TMonitor>) => {
		return <MonitorTemplateAssetBase {...props} agentsOnly={false} />;
	},
);

export const MonitorTimeout = observer(
	<TMonitor extends MonitorBase<TMonitor>>(props: MonitorConfigurationEntryProps<TMonitor, 'timeout'>) => {
		return (
			<FormEntryNew label={i18n('Timeout (sec)')} model={props.monitor} modelField={'timeout'}>
				<AntInputNumber />
			</FormEntryNew>
		);
	},
);

export const MonitorCvid = observer(
	<TMonitor extends MonitorBase<TMonitor>>(props: MonitorConfigurationEntryProps<TMonitor>) => {
		if (props.monitor.id == null) return null;

		return (
			<FormEntryNew label={i18n('CVID')} model={props.monitor} modelField={'id'}>
				<AntInput readOnly={true} />
			</FormEntryNew>
		);
	},
);

export const MonitorErrorSeverity = observer(
	<TMonitor extends MonitorBase<TMonitor>>(props: MonitorConfigurationEntryProps<TMonitor>) => {
		return (
			<FormEntryNew label={i18n('Monitor Error')} model={props.monitor} modelField={'monitorErrorSeverity'}>
				<AntSelect
					dataList={getSeverityDataList()}
					customRenderer={(item: { id: Severity; name: string }) => <SeverityLabel severity={item.id} />}
				/>
			</FormEntryNew>
		);
	},
);

const bConnectionTest = b_.with('monitor-connection-test');

export enum ConnectionTestState {
	Connected = 'connected',
	NotConnected = 'not-connected',
	Error = 'error',
	Connecting = 'connecting',
}

export type ConnectionTestProps = {
	indicatorPosition?: ToolbarItemPosition;
	autoConnect?: boolean;
	model: {
		connectionState: ConnectionTestState;
		connect: () => void;
		canTestConnection: boolean;
	};
};
export const ConnectionTest = observer((props: ConnectionTestProps) => {
	const model = props.model;

	useEffect(() => {
		if (props.autoConnect) {
			model.connect();
		}
	}, [model.connect, props.autoConnect]);

	return (
		<Toolbar appearance={'transparent'}>
			<span
				position={props.indicatorPosition}
				className={bConnectionTest('state', {
					[model.connectionState]: true,
				})}
			>
				{model.connectionState == ConnectionTestState.Connected && i18n('Connected')}
				{model.connectionState == ConnectionTestState.NotConnected && i18n('Not connected')}
				{model.connectionState == ConnectionTestState.Error && i18n('Could not connect')}
			</span>

			<AntButton
				type={model.connectionState == ConnectionTestState.Connected ? 'default' : 'primary'}
				onClick={model.connect}
				disabled={!model.canTestConnection || model.connectionState == ConnectionTestState.Connected}
				loading={model.connectionState == ConnectionTestState.Connecting}
			>
				{model.connectionState == ConnectionTestState.Connecting ? i18n('Connecting...') : i18n('Connect')}
			</AntButton>
		</Toolbar>
	);
});
